import type { UseQueryResult } from "@tanstack/react-query";
import { useContext, useEffect, useMemo, useRef } from "react";
import type { Catalog } from "@/entities/thread";
import { DesktopCatalogGridLayout } from "@/features/catalog/components/desktop/DesktopCatalogGridLayout";
import { DesktopCatalogItem } from "@/features/catalog/components/desktop/DesktopCatalogItem";
import { useFavoriteThreads } from "@/features/catalog/hooks/useFavoriteThreads";
import { useFilteredThreads } from "@/features/catalog/hooks/useFilteredThreads";
import { useOekakiFloor } from "@/features/catalog/hooks/useOekakiFloor";
import {
  createCatalogContext,
  getLastCatalogIds,
  getNewCatalogThreadIds,
  useCatalogStore,
} from "@/features/catalog/stores/catalogStore";
import { catalogContextBoardSlug } from "@/features/catalog/utils/catalogContext";
import {
  initialWheelReloadState,
  recordEdgeWheel,
  resetWheelReloadSequence,
} from "@/features/thread/utils/wheelReload";
import { Message } from "@/shared/ui/feedback";
import { QueryStateBoundary } from "@/shared/ui/feedback/QueryStateBoundary";
import { ModalContext } from "@/shared/ui/overlay/modal-context";

interface DesktopCatalogGridProps {
  query: UseQueryResult<Catalog>;
  onRefresh: () => unknown;
  wheelReloadEnabled?: boolean;
}

export const DesktopCatalogGrid: React.FunctionComponent<
  DesktopCatalogGridProps
> = ({
  query,
  onRefresh,
  wheelReloadEnabled = true,
}: DesktopCatalogGridProps) => {
  const { data, error, isFetching, isLoading, refetch } = query;
  const { isModalOpen } = useContext(ModalContext);
  const wheelStateRef = useRef(initialWheelReloadState());
  const filteredThreads = useFilteredThreads(data?.threads);
  const visibleThreads = useFavoriteThreads(useOekakiFloor(filteredThreads));
  const lastCatalogIds = useCatalogStore((state) => state.lastCatalogIds);
  const currentSort = useCatalogStore((state) => state.currentSort);
  const page = useCatalogStore((state) => state.page);
  const columns = useCatalogStore((state) => state.columns);
  const imageSize = useCatalogStore((state) => state.imageSize);
  const newThreadIds = useMemo(() => {
    const context = createCatalogContext(
      catalogContextBoardSlug(),
      page,
      currentSort,
    );
    return getNewCatalogThreadIds(
      data?.threads.map((thread) => thread.id) ?? [],
      getLastCatalogIds({ lastCatalogIds }, context),
    );
  }, [currentSort, data?.threads, lastCatalogIds, page]);

  useEffect(() => {
    if (!wheelReloadEnabled || isModalOpen) {
      wheelStateRef.current = initialWheelReloadState();
      return;
    }
    const wheel = (event: WheelEvent): void => {
      if (event.shiftKey || event.altKey || event.ctrlKey || event.metaKey)
        return;
      const atEdge =
        event.deltaY < 0
          ? window.scrollY <= 5
          : window.scrollY + window.innerHeight >=
            document.documentElement.scrollHeight - 5;
      const result = recordEdgeWheel(
        wheelStateRef.current,
        event.deltaY,
        atEdge,
        performance.now(),
        event.deltaMode,
      );
      wheelStateRef.current = result.state;
      if (result.reload) void onRefresh();
    };
    const resetWheelState = (): void => {
      wheelStateRef.current = resetWheelReloadSequence(wheelStateRef.current);
    };
    wheelStateRef.current = initialWheelReloadState();
    window.addEventListener("wheel", wheel, { passive: true });
    window.addEventListener("scroll", resetWheelState, { passive: true });
    return (): void => {
      window.removeEventListener("wheel", wheel);
      window.removeEventListener("scroll", resetWheelState);
    };
  }, [isModalOpen, onRefresh, wheelReloadEnabled]);

  return (
    <QueryStateBoundary
      error={error}
      hasData={data !== undefined}
      isLoading={isLoading}
      isRetrying={isFetching}
      message="スレッド一覧の読み込みに失敗しました"
      onRetry={(): void => void refetch()}
    >
      {visibleThreads.length === 0 ? (
        <Message variant="info">スレッドが見つかりません</Message>
      ) : (
        <DesktopCatalogGridLayout columns={columns} imageSize={imageSize}>
          {visibleThreads.map((thread) => (
            <DesktopCatalogItem
              key={thread.id}
              thread={thread}
              isNew={newThreadIds.has(thread.id)}
            />
          ))}
        </DesktopCatalogGridLayout>
      )}
    </QueryStateBoundary>
  );
};
